import { useEffect, useState } from "react";
import { useParams } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Download, FileText, Loader2, Lock, ShieldX, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  publicGetBundle, publicVerifyEmail, publicView, publicDownloadUrl,
} from "@/lib/files-api";

/**
 * Public recipient-facing share-bundle page.
 *
 * Mounted at /share/bundle/:token — outside the AuthenticatedLayout so it is
 * usable by external recipients with no tenant account. The token alone is
 * NOT sufficient when gate_mode === 'email_required'; the user must verify
 * an email that matches one of the bundle's recipients, after which the
 * server sets an HMAC cookie and returns the items list.
 */
export default function PublicShareBundlePage() {
  const { token } = useParams<{ token: string }>();
  const qc = useQueryClient();
  const [emailInput, setEmailInput] = useState("");
  const [verifyError, setVerifyError] = useState<string | null>(null);

  const previewQ = useQuery({
    queryKey: ["public-share", token],
    queryFn: () => publicGetBundle(token!),
    enabled: !!token,
  });

  const verify = useMutation({
    mutationFn: () => publicVerifyEmail(token!, emailInput.trim().toLowerCase()),
    onSuccess: () => {
      setVerifyError(null);
      qc.invalidateQueries({ queryKey: ["public-share", token] });
    },
    onError: (e: Error) => setVerifyError(e.message),
  });

  // Fire view beacon once preview verifies / link-only
  useEffect(() => {
    if (!previewQ.data || previewQ.data.requiresEmail) return;
    publicView(token!);
  }, [previewQ.data, token]);

  if (previewQ.isLoading) {
    return (
      <Centered>
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </Centered>
    );
  }
  if (previewQ.isError || !previewQ.data) {
    return (
      <Centered>
        <ShieldX className="h-10 w-10 text-destructive mb-3" />
        <h1 className="text-xl font-semibold">Share not found</h1>
        <p className="text-sm text-muted-foreground mt-1">
          This link may have been deleted or never existed.
        </p>
      </Centered>
    );
  }

  const d = previewQ.data;
  if (d.revoked) {
    return (
      <Centered>
        <ShieldX className="h-10 w-10 text-destructive mb-3" />
        <h1 className="text-xl font-semibold">This share has been revoked</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {d.orgName} is no longer making these files available.
        </p>
      </Centered>
    );
  }
  if (d.expired) {
    return (
      <Centered>
        <Clock className="h-10 w-10 text-amber-500 mb-3" />
        <h1 className="text-xl font-semibold">This share has expired</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Ask {d.orgName} for a fresh link.
        </p>
      </Centered>
    );
  }

  if (d.requiresEmail) {
    return (
      <Centered>
        <Lock className="h-10 w-10 text-muted-foreground mb-3" />
        <h1 className="text-xl font-semibold">{d.bundleName}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          From {d.orgName}. Enter the email this was sent to:
        </p>
        <div className="flex gap-2 mt-4 w-full max-w-sm">
          <Input
            type="email"
            placeholder="you@example.com"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && verify.mutate()}
          />
          <Button
            disabled={!emailInput || verify.isPending}
            onClick={() => verify.mutate()}
          >
            {verify.isPending
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : "Continue"}
          </Button>
        </div>
        {verifyError && (
          <p className="text-xs text-destructive mt-2">{verifyError}</p>
        )}
      </Centered>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto py-10 px-4 space-y-6">
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">From {d.orgName}</p>
          <h1 className="text-2xl font-semibold mt-1">{d.bundleName}</h1>
          {d.verifiedEmail && (
            <p className="text-xs text-muted-foreground mt-1">Verified as {d.verifiedEmail}</p>
          )}
          {d.message && (
            <div className="rounded-md border bg-muted/40 px-4 py-3 mt-4">
              <p className="text-sm whitespace-pre-wrap">{d.message}</p>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">
              {d.items?.length ?? 0} file{(d.items?.length ?? 0) === 1 ? "" : "s"}
            </h2>
            {typeof d.downloadsRemaining === "number" && (
              <span className="text-xs text-muted-foreground">
                {d.downloadsRemaining} download{d.downloadsRemaining === 1 ? "" : "s"} remaining
              </span>
            )}
          </div>
          <div className="space-y-1">
            {d.items?.map((item) => (
              <div
                key={item.id}
                className="rounded-md border bg-card px-3 py-2 flex items-center gap-3"
              >
                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{item.fileName}</p>
                  <p className="text-xs text-muted-foreground">
                    {item.mimeType ?? "file"}
                    {item.sizeBytes ? ` · ${formatBytes(item.sizeBytes)}` : ""}
                  </p>
                </div>
                <a
                  href={publicDownloadUrl(token!, item.id)}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
                >
                  <Download className="h-3.5 w-3.5" />
                  Download
                </a>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
      {children}
    </div>
  );
}

function formatBytes(n: number): string {
  const units = ["B", "KB", "MB", "GB"];
  let v = n; let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}
